import { compareSmetaGroupSort } from "./smetaNewTableRows.js";
import {
  buildResidentSmetaFilterOptionsFromRaw,
  collectResidentSmetaExpandableIds,
  formatResidentSmetaBlockProjectIdLine,
  formatResidentSmetaBlockTitle,
  getResidentSmetaExpandedIdsForFilters,
  isResidentSmetaFilterValueActive,
} from "./residentSmetaTree.js";

export {
  buildResidentSmetaFilterOptionsFromRaw,
  collectResidentSmetaExpandableIds,
  isResidentSmetaFilterValueActive,
};

function roundCost(n) {
  return Math.round(n * 100) / 100;
}

function emptyCostBucket() {
  return { wsum: 0, msum: 0, ttl: 0, ttl_nds: 0 };
}

function addRowCostToBucket(bucket, row) {
  if (row.wsum_no_nds != null) bucket.wsum += Number(row.wsum_no_nds) || 0;
  if (row.msum_no_nds != null) bucket.msum += Number(row.msum_no_nds) || 0;
  if (row.ttl_no_nds != null) bucket.ttl += Number(row.ttl_no_nds) || 0;
  if (row.ttl != null) bucket.ttl_nds += Number(row.ttl) || 0;
}

function applyCostsToNode(node, bucket) {
  node.wsum_cost = roundCost(bucket.wsum);
  node.msum_cost = roundCost(bucket.msum);
  node.total_cost = roundCost(bucket.ttl);
  node.total_cost_with_nds = roundCost(bucket.ttl_nds);
}

function getBlockKey(row) {
  if (row.project_entrance_id != null && row.project_entrance_id !== "") {
    return `pe:${row.project_entrance_id}`;
  }
  return `legacy:${row.project_id}__${row.entrance ?? ""}`;
}

export function parseResidentUspPath(row) {
  const raw = row?.usp_path;
  let path = [];
  if (Array.isArray(raw)) path = raw;
  else if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) path = parsed;
    } catch {
      path = [];
    }
  }
  // SQL может повторить один guid в пути (несколько project_id → один work_guid).
  // Без сжатия фронт вешает узел сам на себя: «Блок 1» → «Блок 1».
  const seen = new Set();
  const unique = [];
  for (const step of path) {
    const guid = String(step?.guid || "");
    if (!guid || seen.has(guid)) continue;
    seen.add(guid);
    unique.push(step);
  }
  return unique;
}

function ensureChild(parent, id, factory) {
  if (!parent._index) parent._index = new Map();
  let node = parent._index.get(id);
  if (!node) {
    node = factory();
    parent._index.set(id, node);
    parent.children.push(node);
  }
  return node;
}

function stripIndex(node) {
  delete node._index;
  if (Array.isArray(node.children)) {
    for (const ch of node.children) stripIndex(ch);
  }
}

function rollupNode(node) {
  if (node.node_type === "work") return;
  const bucket = emptyCostBucket();
  for (const ch of node.children || []) {
    rollupNode(ch);
    bucket.wsum += Number(ch.wsum_cost) || 0;
    bucket.msum += Number(ch.msum_cost) || 0;
    bucket.ttl += Number(ch.total_cost) || 0;
    bucket.ttl_nds += Number(ch.total_cost_with_nds) || 0;
  }
  applyCostsToNode(node, bucket);
}

function buildWorkNodes(groupNode, rowsOfGroup, tableRows) {
  const byWork = new Map();
  for (const row of rowsOfGroup) {
    const wkey = `${row.project_id ?? ""}|${row.project_entrance_id ?? ""}|${row.level_num ?? ""}|${row.work_set_id ?? ""}|${row.source_work_set_check_group_id ?? ""}`;
    if (!byWork.has(wkey)) {
      byWork.set(wkey, []);
    }
    byWork.get(wkey).push(row);
  }

  const workNodes = [];
  const entries = [...byWork.entries()].sort((a, b) => {
    const la = a[1][0]?.work_set_name || a[1][0]?.wname || "";
    const lb = b[1][0]?.work_set_name || b[1][0]?.wname || "";
    return String(la).localeCompare(String(lb), "ru");
  });

  for (const [wkey, workRows] of entries) {
    const sample = workRows[0];
    const tableForWork = (tableRows || []).filter((r) => {
      if (r.isGroupHeader) return false;
      return (
        String(r.project_id ?? "") === String(sample.project_id ?? "") &&
        String(r.project_entrance_id ?? "") === String(sample.project_entrance_id ?? "") &&
        String(r.level_num ?? "") === String(sample.level_num ?? "") &&
        String(r.work_set_id ?? "") === String(sample.work_set_id ?? "") &&
        String(r.work_set_check_group_id ?? "") === String(sample.work_set_check_group_id ?? "")
      );
    });

    const bucket = emptyCostBucket();
    const materialRows = [];
    for (const r of tableForWork) {
      addRowCostToBucket(bucket, r);
      if (r.material_id != null && Number(r.material_id) !== 0) {
        materialRows.push(r);
      }
    }

    const materialsNode = {
      id: `mat:${groupNode.id}:${wkey}`,
      node_type: "materials",
      label: "Материалы",
      expanded: false,
      work_set_id: sample.work_set_id,
      project_id: groupNode.project_id,
      project_entrance_id: groupNode.project_entrance_id,
      entrance: groupNode.entrance,
      level_num: groupNode.level_num,
      work_set_check_group_id: groupNode.work_set_check_group_id,
      materialRows,
      children: [],
      msum_cost: roundCost(bucket.msum),
    };

    const qtyRow = tableForWork.find((r) => r.wqty != null) || sample;
    const workNode = {
      id: `wrk:${groupNode.id}:${wkey}`,
      node_type: "work",
      label: sample.work_set_name || sample.wname || "Работа",
      wunt: qtyRow.wunt || sample.work_set_unit_name || "",
      wqty: qtyRow.wqty ?? sample.total_work_amount,
      wprc_no_nds: qtyRow.wprc_no_nds ?? sample.total_work_price_without_nds,
      expanded: false,
      work_set_id: sample.work_set_id,
      project_id: groupNode.project_id,
      project_entrance_id: groupNode.project_entrance_id,
      entrance: groupNode.entrance,
      level_num: groupNode.level_num,
      work_set_check_group_id: groupNode.work_set_check_group_id,
      children: [materialsNode],
    };
    applyCostsToNode(workNode, bucket);
    workNodes.push(workNode);
  }

  return workNodes;
}

function attachGroups(parent, rows, tableRows) {
  const byGroup = new Map();
  for (const row of rows) {
    const gid = String(row.work_set_check_group_id ?? "nogroup");
    if (!byGroup.has(gid)) byGroup.set(gid, []);
    byGroup.get(gid).push(row);
  }

  const groups = [...byGroup.entries()].map(([gid, groupRows]) => {
    const sample = groupRows[0];
    const groupNode = {
      id: `g:${parent.id}:${gid}:${sample.project_id ?? ""}:${sample.project_entrance_id ?? ""}:${sample.level_num ?? ""}`,
      node_type: "group",
      label: sample.work_set_check_group_name || "Группа работ",
      expanded: false,
      project_id: sample.project_id,
      project_entrance_id: sample.project_entrance_id,
      entrance: sample.entrance,
      level_num: sample.level_num,
      work_set_check_group_id: sample.work_set_check_group_id,
      group_order_num: sample.group_order_num,
      source_work_set_check_group_id: sample.source_work_set_check_group_id,
      children: [],
    };
    groupNode.children = buildWorkNodes(groupNode, groupRows, tableRows);
    return groupNode;
  });

  groups.sort(compareSmetaGroupSort);
  parent.children.push(...groups);
}

/**
 * Дерево: ЖК → путь УСП → «Интеграция SBS» → группа → работа → материалы.
 * Непривязанное: ЖК → «Не привязано к УСП» → причина → тип → блок → этаж → группа → работа.
 */
export function buildResidentUspSmetaTree(rawRows, residentName, tableRows = []) {
  const root = {
    id: "resident",
    node_type: "resident",
    label: residentName || rawRows?.[0]?.resident_name || "ЖК",
    expanded: false,
    children: [],
  };

  if (!rawRows?.length) {
    applyCostsToNode(root, emptyCostBucket());
    return [root];
  }

  const mapped = [];
  const unmapped = [];
  for (const row of rawRows) {
    const path = parseResidentUspPath(row);
    if (path.length && row.usp_work_guid) mapped.push(row);
    else unmapped.push(row);
  }

  const mappedByPath = new Map();
  for (const row of mapped) {
    const path = parseResidentUspPath(row);
    const key = path.map((s) => String(s.guid || "")).join("/");
    if (!mappedByPath.has(key)) mappedByPath.set(key, { path, rows: [] });
    mappedByPath.get(key).rows.push(row);
  }

  for (const { path, rows } of mappedByPath.values()) {
    let parent = root;
    for (const step of path) {
      const guid = String(step.guid || "");
      const id = `usp:${guid}`;
      const itsWork = Boolean(step.its_work);
      parent = ensureChild(parent, id, () => ({
        id,
        node_type: itsWork ? "usp_work" : "usp",
        label: step.name || "—",
        usp_guid: guid,
        expanded: false,
        children: [],
      }));
    }
    attachGroups(parent, rows, tableRows);
  }

  if (unmapped.length) {
    const unmappedRoot = ensureChild(root, "unmapped", () => ({
      id: "unmapped",
      node_type: "unmapped",
      label: "Не привязано к УСП",
      expanded: false,
      children: [],
    }));

    const byReason = new Map();
    for (const row of unmapped) {
      const reason = row.unmapped_reason || "Не привязано к УСП";
      if (!byReason.has(reason)) byReason.set(reason, []);
      byReason.get(reason).push(row);
    }

    for (const [reason, reasonRows] of [...byReason.entries()].sort((a, b) =>
      a[0].localeCompare(b[0], "ru")
    )) {
      const reasonNode = ensureChild(unmappedRoot, `reason:${reason}`, () => ({
        id: `reason:${reason}`,
        node_type: "unmapped_reason",
        label: reason,
        expanded: false,
        children: [],
      }));

      const byType = new Map();
      for (const row of reasonRows) {
        const tname = row.project_type_name || "—";
        if (!byType.has(tname)) byType.set(tname, []);
        byType.get(tname).push(row);
      }

      for (const [tname, typeRows] of [...byType.entries()].sort((a, b) =>
        a[0].localeCompare(b[0], "ru")
      )) {
        const typeNode = ensureChild(reasonNode, `type:${tname}`, () => ({
          id: `type:${tname}`,
          node_type: "project_type",
          label: tname,
          project_type_id: typeRows[0]?.project_type_id,
          expanded: false,
          children: [],
        }));

        const byBlock = new Map();
        for (const row of typeRows) {
          const bkey = getBlockKey(row);
          if (!byBlock.has(bkey)) byBlock.set(bkey, []);
          byBlock.get(bkey).push(row);
        }

        for (const [bkey, blockRows] of byBlock.entries()) {
          const sample = blockRows[0];
          const project_id = sample?.project_id;
          const blockNode = ensureChild(typeNode, `b:${bkey}`, () => ({
            id: `b:${bkey}`,
            node_type: "block",
            label: formatResidentSmetaBlockTitle(sample, project_id),
            projectIdLine: formatResidentSmetaBlockProjectIdLine(sample, project_id),
            project_id,
            project_entrance_id: sample?.project_entrance_id,
            entrance: sample?.entrance,
            expanded: false,
            children: [],
          }));

          const byFloor = new Map();
          for (const row of blockRows) {
            const levelKey = String(row.level_num ?? "");
            if (!byFloor.has(levelKey)) byFloor.set(levelKey, []);
            byFloor.get(levelKey).push(row);
          }

          const floors = [...byFloor.entries()]
            .map(([levelKey, floorRows]) => {
              const floorSample = floorRows[0];
              const level_num = floorSample?.level_num;
              const floorNode = {
                id: `f:${blockNode.id}:${levelKey}`,
                node_type: "floor",
                label: `Этаж ${level_num ?? "—"}`,
                expanded: false,
                project_id: blockNode.project_id,
                project_entrance_id: blockNode.project_entrance_id,
                entrance: blockNode.entrance,
                level_num,
                floor_map_id: floorSample?.floor_map_id ?? null,
                children: [],
              };
              attachGroups(floorNode, floorRows, tableRows);
              return floorNode;
            })
            .sort((a, b) => Number(a.level_num ?? 0) - Number(b.level_num ?? 0));

          blockNode.children.push(...floors);
        }
      }
    }
  }

  stripIndex(root);
  const sortKids = (node) => {
    const kids = node.children || [];
    if (node.node_type === "usp_work" || node.node_type === "floor") {
      kids.sort(compareSmetaGroupSort);
    } else if (
      node.node_type === "resident" ||
      node.node_type === "usp" ||
      node.node_type === "unmapped" ||
      node.node_type === "unmapped_reason" ||
      node.node_type === "project_type" ||
      node.node_type === "block"
    ) {
      kids.sort((a, b) => {
        if (a.node_type === "unmapped") return 1;
        if (b.node_type === "unmapped") return -1;
        return String(a.label || "").localeCompare(String(b.label || ""), "ru");
      });
    }
    for (const ch of kids) sortKids(ch);
  };
  sortKids(root);
  rollupNode(root);
  return [root];
}

export function getResidentUspSmetaExpandedIdsForFilters(treeRoots, filters) {
  const ids = getResidentSmetaExpandedIdsForFilters(treeRoots, filters);
  if (ids.size > 0) return ids;

  const hasFilter = [
    filters?.project_type_id,
    filters?.project_entrance_id,
    filters?.floor_map_id,
    filters?.level_num,
    filters?.work_set_check_group_id,
    filters?.work_set_id,
    filters?.material_id,
  ].some(isResidentSmetaFilterValueActive);

  if (!hasFilter) return ids;

  const walk = (nodes) => {
    if (!nodes?.length) return;
    for (const n of nodes) {
      if (
        n.node_type === "usp" ||
        n.node_type === "usp_work" ||
        n.node_type === "unmapped" ||
        n.node_type === "unmapped_reason" ||
        n.node_type === "resident"
      ) {
        ids.add(n.id);
      }
      walk(n.children);
    }
  };
  walk(treeRoots);
  return ids;
}
